import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Card,
  IconButton,
  Rating,
  Grid2,
  Tooltip,
  Typography,
  Stack
} from '@mui/material';
import {
  Clock,
  Book1,
  Heart,
  LanguageSquare,
  ArrowLeft,
  Note1,
  Book,
  SidebarLeft
} from 'iconsax-react';
import NoImage from 'assets/images/homepage/NoCourseImage.jpg';
import useAuth from 'hooks/useAuth';
import { useEffect, useState } from 'react';
import Snackbar from 'utils/Snackbar';
import { addFavoriteCourse } from 'services/course.service';
import { getFeedback } from 'services/review.service';
import { getNormalizeFileUrl } from 'utils/getNormalizeFileUrl';

const HeaderOption = (props: any) => {
  const { courseName, courseImage, totalSections, disableIcons } = props;

  const { user }: any = useAuth();

  const navigate = useNavigate()

  const [favorite, setFavorite] = useState(props?.favorite?.includes(user?._id));
  const [averageRatings, setAverageRatings] = useState(0);

  const getRatings = async () => {

    return getFeedback({ courseId: props?.courseId })
      .then((res: any) => {
        if (res.status === 200) {
          let response = res.data.data
          setAverageRatings(Number(Number(response.average).toFixed(1)))
          return res.data.data;
        } else {
          Snackbar(res.data.message, 'error');
        }
      })
      .catch((err: any) => {
        if (err?.response?.status === 403) {
          const errorMessage = err?.response?.data?.message || "You don't have the required permissions to perform this action.";
          Snackbar(errorMessage, 'warning');
        } else {
          const errorMessage = err?.response?.data?.message || 'An unexpected error occurred. Please try again later.';
          Snackbar(errorMessage, 'error');
        }
      });
  };

  useEffect(() => {
    props?.courseId && getRatings()
  }, [props?.courseId])

  const handleLikeClick = () => {
    addFavoriteCourse({
      userId: user?._id,
      organizationId: user.organizationId?._id,
      courseId: props?.courseId
    })
      .then((res: any) => {
        if (res?.status === 200) {
          setFavorite(!favorite);
          Snackbar(res?.data?.message, 'success');
        } else;
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          const errorMessage = err?.response?.data?.message || "You don't have the required permissions to perform this action.";
          Snackbar(errorMessage, 'warning');
        } else {
          const errorMessage = err?.response?.data?.message || 'An unexpected error occurred. Please try again later.';
          Snackbar(errorMessage, 'error');
        }
      });
  };


  return (
    <Card sx={{ p: { xs: 1, sm: 2 }, mb: 1, borderRadius: 1.5 }}>
      <Grid2 container alignItems="center">
        {/* Left section with back button */}
        <Grid2 size='auto'>
          <ArrowLeft
            color='#222222'
            size={30}
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (props.navigationState) {
                navigate(props.navigationPath, { state: props.navigationState });
              } else {
                navigate(props.navigationPath);
              }
            }}
          />
        </Grid2>

        {/* Course image */}
        <Grid2 size='auto' sx={{ ml: { xs: 1, sm: 2 } }} >
          <Avatar
            variant="rounded"
            src={getNormalizeFileUrl(courseImage, NoImage)}
            alt="course image"
            sx={{
              width: { xs: 100, sm: 150 },
              height: { xs: 60, sm: 80 },
              objectFit: 'cover',
              borderRadius: 2
            }}
          />
        </Grid2>

        {/* Course info */}
        <Grid2 size='grow' sx={{ ml: { xs: 1, sm: 2 } }} >
          <Stack spacing={1}>
            <Typography
              color="#222222"
              fontWeight={600}
              fontSize={{ xs: '18px', sm: '22px' }}
              lineHeight={{ xs: '21px', sm: '25px' }}
              noWrap
            >
              {courseName}
            </Typography>

            {/* Course Stats - Now visible on all screens with responsive layout */}
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 2 }}
              flexWrap="wrap"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Clock size={18} color="#222222" />
                <Typography
                  variant="body2"
                  color="#666666"
                  sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                >
                  {props?.courseDuration}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Book1 size={18} color="#222222" />
                <Typography
                  variant="body2"
                  color="#666666"
                  sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                >
                  {totalSections} Sections
                </Typography>
              </Stack>

              {
                props?.showRatings &&
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    variant="body2"
                    color="#222222"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                  >
                    {averageRatings}/5
                  </Typography>
                  <Rating
                    value={averageRatings}
                    readOnly
                    size="small"
                    precision={0.1}
                  />
                </Stack>
              }
            </Stack>
          </Stack>
        </Grid2>

        {/* Action buttons */}
        <Grid2 size='auto' >
          <Stack
            direction="row"
            spacing={{ xs: 0.5, sm: 1 }}
          >
            {user?.type === 'student' && <Tooltip title="Favorites">
              <IconButton

                sx={{
                  border: '0.5px solid',
                  borderColor: '#D1D1D1',
                  borderRadius: '10px'
                }}
                onClick={handleLikeClick}
              >
                <Heart
                  color={favorite ? 'red' : "black"}
                  variant={favorite ? 'Bold' : "Outline"}
                  size={20}
                />
              </IconButton>
            </Tooltip>}

            {
              props?.showDictionaryIcon &&
              <Tooltip title="Dictionary">
                <IconButton

                  sx={{
                    border: '0.5px solid',
                    borderColor: '#D1D1D1',
                    borderRadius: '10px'
                  }}
                  disabled={disableIcons}
                  onClick={() => props.setSection({ showDictionary: true })}
                >
                  <LanguageSquare color={disableIcons ? "#E0E0E0" : "#222222"} size={20} />
                </IconButton>
              </Tooltip>
            }
            {
              props?.showBookmarkIcon &&
              <Tooltip title="Bookmarked">
                <IconButton

                  disabled={disableIcons}
                  sx={{
                    border: '0.5px solid',
                    borderColor: '#D1D1D1',
                    borderRadius: '10px'
                  }}
                  onClick={() => props.setSection({ bookmarkOpen: true })}
                >
                  <Book color={disableIcons ? "#E0E0E0" : "#222222"} size={20} />
                </IconButton>
              </Tooltip>
            }
            {
              props?.showNoteIcon &&
              <Tooltip title="Notes">
                <IconButton

                  disabled={disableIcons}
                  sx={{
                    border: '0.5px solid',
                    borderColor: '#D1D1D1',
                    borderRadius: '10px'
                  }}
                  onClick={() => props.setSection({ noteOpen: true })}
                >
                  <Note1 color={disableIcons ? "#E0E0E0" : "#222222"} size={20} />
                </IconButton>
              </Tooltip>
            }

            <Tooltip title="Sections">
              <IconButton

                sx={{
                  border: '0.5px solid',
                  borderColor: '#D1D1D1',
                  borderRadius: '10px'
                }}
                onClick={() => props.setSection({ sectionOpen: true })}
              >
                <SidebarLeft color="#222222" size={20} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid2>
      </Grid2>
    </Card>

  );
};

export default HeaderOption;

